/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import type { AttributeModel } from '@/api/types/products-types';
import Price from '@/components/AZCustomComponent/Price';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { RatingsReview } from '@/components/AZCustomComponent/RatingsReview';
import Title from '@/components/AZCustomComponent/Title';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { Drawer } from '@/components/Drawer/Drawer';
import { Table } from '@/components/Table/Table';
import { TableBody } from '@/components/TableBody/TableBody';
import { TableCell } from '@/components/TableCell/TableCell';
import { TableRow } from '@/components/TableRow/TableRow';
import { routePaths } from '@/constants/routePaths';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import type { AddToCartResponse } from '@/types/reduxStore/cartUpdate';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLabels } from '@/hooks/useLabels';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { useProductComparison } from '@/features/product/api/getProductComparison';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import type {
  AZBadgesFlagVOFromAPI,
  SkuDealsFromAPI,
  SkuPricingAndAvailabilityFromAPI,
} from '@/types/availability';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ComparisonTableBodyCell } from './components/ComparisonTableBodyCell/ComparisonTableBodyCell';
import { ComparisonTableHeaderCell } from './components/ComparisonTableHeaderCell/ComparisonTableHeaderCell';
import { ShopNowButton } from './components/ShopNowButton/ShopNowButton';
import sharedStyles from './sharedStyles.module.scss';
import styles from './style.module.scss';
import { Skeleton, Text } from '@az/starc-ui';
import type { PDPComparisonAnalytics } from '@/types/analytics';
import { useFeatureFlag } from '@/features/globalConfig';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { MINI_CART_TITLE_ID } from '@/features/miniCartFlyout';
import { CUSTOMER_REVIEWS_HASH } from '@/features/customReviews/constants/sectionHash';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { type VehicleFit } from '@/api/types/browse-search-types';
import { useAppData } from '@/hooks/useAppData';
import { type CertonaRecommendation } from '@/features/certona';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { getFitmentBadgeState } from './utils/getFitmentBadgeState';

export type PDPComparisonTableProps = {
  parentBrandName: string;
  subBrandName: string | undefined;
  productId: string;
  skuId: string;
  partNumber: string;
  productName: string;
  price: number | undefined;
  averageRating: number | undefined;
  totalReviews: number | undefined;
  productAttributes: AttributeModel[] | undefined;
  location: string | undefined;
  itemWeight: string | undefined;
  warranty: string | undefined;
  productImage: string | null;
  hasPreferredVehicle: boolean;
  isCurrentSkuAppProduct: boolean;
  isCurrentSkuVehicleSpecific: boolean;
  currentSkuFitsVehicle: boolean;
  currentSkuVehicleFitment: VehicleFit | undefined;
  analyticsData: PDPComparisonAnalytics;
  certonaRecommendations?: CertonaRecommendation[];
};

type ProductInformation = {
  name: string;
  image: string;
  skuPricingAndAvailability: SkuPricingAndAvailabilityFromAPI | undefined;
  aZBadgesFlagVO: AZBadgesFlagVOFromAPI | undefined;
  successData: AddToCartResponse & { quantity: string };
};

type ComparisonDisplayPriceOptions = {
  skuDeal: SkuDealsFromAPI | undefined;
  skuPricingAndAvailability: SkuPricingAndAvailabilityFromAPI | undefined;
  fallbackPrice?: number;
};

const LazyMiniCartFlyoutComp = dynamic(
  () =>
    import('@/features/miniCartFlyout/components/MiniCartFlyoutComp').then(
      (mod) => mod.MiniCartFlyoutComp,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const labelMap = {
  customerRating: 'label_PDPComparison_customerRating',
  price: 'label_PDPComparison_price',
};

function PDPComparisonTableComp({
  skuId,
  partNumber,
  productName,
  price,
  averageRating,
  totalReviews,
  subBrandName,
  parentBrandName,
  productId,
  productAttributes,
  location,
  itemWeight,
  warranty,
  productImage,
  hasPreferredVehicle,
  isCurrentSkuAppProduct,
  isCurrentSkuVehicleSpecific,
  currentSkuFitsVehicle,
  currentSkuVehicleFitment,
  analyticsData,
  certonaRecommendations,
}: PDPComparisonTableProps) {
  const labels = useLabels(labelMap);
  const { appData } = useAppData();
  const { deviceType } = appData;
  const isABot = deviceType === 'bot';
  const router = useRouter();
  const { acesEnabled } = useAcesFlags();
  const lazyMiniCartModalRef = useRef<HTMLDivElement>(null);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [selectedProductInformation, setSelectedProductInformation] =
    useState<ProductInformation | null>(null);
  const { data: headerData } = useHeaderData();
  const enablePdpComparisonTableDescription =
    useFeatureFlag('ENABLE_PDP_COMPARISON_TABLE_DESCRIPTION') === 'true';
  const locale = useLocale();
  const { setPageSpecificData } = usePageSpecificData();
  const isPdpLowerV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const [queryEnabled, setQueryEnabled] = useState(() => isABot);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;

      if (hash === CUSTOMER_REVIEWS_HASH) {
        setQueryEnabled(true);
        return;
      }
    }

    if (!isABot) {
      const handleEnableQuery = () => {
        setQueryEnabled(true);
        window.removeEventListener('scroll', handleEnableQuery);
      };

      window.addEventListener('scroll', handleEnableQuery);

      return () => {
        window.removeEventListener('scroll', handleEnableQuery);
      };
    }
  }, [isABot]);

  const { data: productComparisonData } = useProductComparison({
    parentBrandName,
    subBrandName,
    productId,
    acesEnabled,
    skuId,
    country: getCountryFromLocale(locale),
    enabled: queryEnabled,
  });

  const attributes = useMemo(() => {
    const attributesObject: Record<string, string | null> = {};
    for (const attribute of productAttributes ?? []) {
      if (attribute.name) {
        attributesObject[attribute.name] = attribute.value ?? null;
      }
    }
    return attributesObject;
  }, [productAttributes]);

  const comparisonAttributeNames = productComparisonData?.attributeNames ?? [];

  const currentSkuAttributeOverrides: Record<string, string | undefined> = {
    location,
    weight: itemWeight,
    warranty,
  };

  const onPDPComparisonLinkClick = useCallback(() => {
    setPageSpecificData({
      productFindingMethod: 'Finder',
      productRecommendationInteraction: 'productRecommendationInteraction',
      pfmLocation: 'PDP',
      productRecommendationType: 'PDP: Recommended For You',
      finderFeature: 'PDP Comparison Chart',
    });
  }, [setPageSpecificData]);

  const {
    data: currentSkuDetails,
    refetch: refetchSKUDetails,
    isFetching: isCurrentSkuDetailsFetching,
  } = useProductSkuDetails({
    skuIds: [skuId],
  });

  const { data: currentSkuDeals, isFetching: isCurrentSkuDealsFetching } = useProductSkuDeals({
    skuIds: [skuId],
    enabled: queryEnabled && Boolean(skuId),
  });

  const comparisonSkuIds = (productComparisonData?.skus ?? []).flatMap((sku) =>
    sku.skuId ? [sku.skuId] : [],
  );

  const { data: comparisonSkuDetails, isFetching: isComparisonSkuDetailsFetching } =
    useProductSkuDetails({
      skuIds: productComparisonData?.skus
        ? productComparisonData.skus.map((sku) => sku.skuId ?? '')
        : null,
      enabled: (productComparisonData?.skus?.length ?? 0) > 0,
    });

  const { data: comparisonSkuDeals, isFetching: isComparisonSkuDealsFetching } = useProductSkuDeals(
    {
      skuIds: comparisonSkuIds,
      enabled: queryEnabled && comparisonSkuIds.length > 0,
    },
  );

  const currentSkuDisplayPrice = getComparisonDisplayPrice({
    skuDeal: currentSkuDeals?.[0],
    skuPricingAndAvailability: currentSkuDetails?.[0]?.skuPricingAndAvailability,
    fallbackPrice: price,
  });
  const isCurrentSkuPriceFetching = isCurrentSkuDetailsFetching || isCurrentSkuDealsFetching;
  const isComparisonSkuPriceFetching =
    isComparisonSkuDetailsFetching || isComparisonSkuDealsFetching;
  const currentSkuFitment = getFitmentBadgeState({
    hasPreferredVehicle,
    isAppProduct: isCurrentSkuAppProduct,
    fitsVehicle: currentSkuFitsVehicle,
    isVehicleSpecific: isCurrentSkuVehicleSpecific,
    vehicleFit: currentSkuVehicleFitment,
  });

  const submitToCart = useCallback(() => {
    router.push(routePaths.cart);
  }, [router]);

  const handleOpenMinicart = useCallback((info: ProductInformation) => {
    setSelectedProductInformation({
      ...info,
    });
    setShowMiniCart(true);
  }, []);

  const handleCloseMiniCart = useCallback(() => {
    setShowMiniCart(false);
    setSelectedProductInformation(null);
  }, []);

  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);

  return Array.isArray(productComparisonData?.attributeNames) &&
    productComparisonData.skus?.length ? (
    <div
      className={isPdpLowerV2Enabled ? styles.comparisonContainerV2 : styles.comparisonContainer}
    >
      <Title
        firstText={productComparisonData.tableName}
        firstTextClass={styles.comparisonTableTitle}
      />
      {enablePdpComparisonTableDescription && (
        <Text className={styles.comparisonTableDescription} size="087" maxLines={2}>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
          ut labore. The big red
        </Text>
      )}
      <div className={styles.overflowShadowContainer}>
        <div className={styles.overflowShadow} />
        <div className={styles.tableWrapper}>
          <Table>
            <thead className={styles.comparisonTableHeader}>
              <TableRow>
                <TableCell component="td"></TableCell>

                <ComparisonTableHeaderCell
                  imageURL={productImage}
                  productName={productName}
                  productURL={null}
                  isCurrentSKU
                  onPDPComparisonLinkClick={onPDPComparisonLinkClick}
                />

                {productComparisonData.skus.map((sku) => (
                  <ComparisonTableHeaderCell
                    key={sku.skuId}
                    imageURL={sku.skuImgUrl}
                    productName={sku.skuName}
                    productURL={sku.skuSeoUrl}
                    onPDPComparisonLinkClick={onPDPComparisonLinkClick}
                  />
                ))}
              </TableRow>
            </thead>

            <TableBody className={styles.comparisonTableBody}>
              <TableRow className={styles.buttonsRow}>
                <TableCell></TableCell>

                <ShopNowButton
                  skuId={skuId}
                  skuDetails={currentSkuDetails?.[0]}
                  productId={productId}
                  partNumber={partNumber}
                  price={currentSkuDisplayPrice}
                  showFitmentBadge={currentSkuFitment.showFitmentBadge}
                  fitsVehicle={currentSkuFitment.fitsVehicle}
                  vehicleFitment={currentSkuFitment.vehicleFitment}
                  analyticsData={analyticsData}
                  openMiniCart={(data) => {
                    handleOpenMinicart({
                      name: productName,
                      image: productImage ?? '',
                      skuPricingAndAvailability: currentSkuDetails?.[0].skuPricingAndAvailability,
                      aZBadgesFlagVO: currentSkuDetails?.[0].aZBadgesFlagVO ?? undefined,
                      successData: data,
                    });

                    void refetchSKUDetails();
                  }}
                />

                {productComparisonData.skus.map((sku, i) => {
                  const comparisonSkuDisplayPrice = getComparisonDisplayPrice({
                    skuDeal: comparisonSkuDeals?.[i],
                    skuPricingAndAvailability: comparisonSkuDetails?.[i]?.skuPricingAndAvailability,
                  });

                  const rowFitment = getFitmentBadgeState({
                    hasPreferredVehicle,
                    isAppProduct: sku.recordType === 'app',
                    fitsVehicle: sku.vehicleFit === 'FITS',
                    isVehicleSpecific: sku.vehicleFitment === 'VEHICLE_SPECIFIC',
                    vehicleFit: sku.vehicleFit,
                  });

                  return (
                    <ShopNowButton
                      key={sku.skuId}
                      skuDetails={comparisonSkuDetails?.[i]}
                      skuId={sku.skuId ?? ''}
                      partNumber={sku.partNumber}
                      productId={productId}
                      price={comparisonSkuDisplayPrice}
                      showFitmentBadge={rowFitment.showFitmentBadge}
                      fitsVehicle={rowFitment.fitsVehicle}
                      vehicleFitment={rowFitment.vehicleFitment}
                      analyticsData={analyticsData}
                      openMiniCart={(data) =>
                        handleOpenMinicart({
                          name: sku.skuName ?? '',
                          image: sku.skuImgUrl ?? '',
                          skuPricingAndAvailability:
                            comparisonSkuDetails?.[i].skuPricingAndAvailability,
                          aZBadgesFlagVO: comparisonSkuDetails?.[i].aZBadgesFlagVO ?? undefined,
                          successData: data,
                        })
                      }
                    />
                  );
                })}
              </TableRow>

              <TableRow className={styles.attributesRow}>
                <TableCell className={sharedStyles.comparisonCell} component="th" scope="row">
                  <p className={cx(sharedStyles.cellText, styles.attributeName)}>
                    {labels.customerRating}
                  </p>
                </TableCell>

                <ComparisonTableBodyCell
                  attributeValue={
                    shouldShowValue(totalReviews) ? (
                      <RatingsReview
                        averageRating={`${averageRating ?? 0}`}
                        totalReviews={`${totalReviews ?? 0}`}
                        containerClassName={styles.rating}
                        showStarFractions
                      />
                    ) : null
                  }
                />

                {productComparisonData.skus.map((sku) => (
                  <ComparisonTableBodyCell
                    key={sku.skuId}
                    attributeValue={
                      shouldShowValue(sku.totalReviewsCount) ? (
                        <RatingsReview
                          averageRating={`${sku.avgRating ?? 0}`}
                          totalReviews={`${sku.totalReviewsCount ?? 0}`}
                          containerClassName={styles.rating}
                          showStarFractions
                        />
                      ) : null
                    }
                  />
                ))}
              </TableRow>

              <TableRow className={styles.attributesRow}>
                <TableCell className={sharedStyles.comparisonCell} component="th" scope="row">
                  <p className={cx(sharedStyles.cellText, styles.attributeName)}>{labels.price}</p>
                </TableCell>

                <ComparisonTableBodyCell
                  attributeValue={renderComparisonPrice(
                    currentSkuDisplayPrice,
                    isCurrentSkuPriceFetching,
                  )}
                />

                {productComparisonData.skus.map((sku, i) => {
                  const comparisonSkuDisplayPrice = getComparisonDisplayPrice({
                    skuDeal: comparisonSkuDeals?.[i],
                    skuPricingAndAvailability: comparisonSkuDetails?.[i]?.skuPricingAndAvailability,
                  });

                  return (
                    <ComparisonTableBodyCell
                      key={sku.skuId}
                      attributeValue={renderComparisonPrice(
                        comparisonSkuDisplayPrice,
                        isComparisonSkuPriceFetching,
                      )}
                    />
                  );
                })}
              </TableRow>

              {comparisonAttributeNames.map((attributeName, attributeIndex) => (
                <TableRow key={attributeName} className={styles.attributesRow}>
                  <TableCell className={sharedStyles.comparisonCell} component="th" scope="row">
                    <p className={cx(sharedStyles.cellText, styles.attributeName)}>
                      {attributeName}
                    </p>
                  </TableCell>

                  <ComparisonTableBodyCell
                    attributeValue={
                      currentSkuAttributeOverrides[attributeName.toLowerCase()] ||
                      attributes[attributeName]
                    }
                  />

                  {productComparisonData.skus?.map((sku) => (
                    <ComparisonTableBodyCell
                      key={sku.skuId}
                      attributeValue={sku.attributeValues[attributeIndex]}
                    />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Drawer
        anchor="right"
        open={showMiniCart}
        onClose={handleCloseMiniCart}
        drawerRef={lazyMiniCartModalRef}
      >
        <TrapFocus open={showMiniCart}>
          <div
            className={styles.miniCartFlyoutDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby={MINI_CART_TITLE_ID}
          >
            {showMiniCart && !!selectedProductInformation && (
              <LazyMiniCartFlyoutComp
                handleClose={handleCloseMiniCart}
                isModalOpen={showMiniCart}
                productTitle={selectedProductInformation.name}
                image={selectedProductInformation.image}
                successData={selectedProductInformation.successData}
                productDetails={{
                  productPartType: selectedProductInformation.successData.originalPartTypeId,
                  originalPartTypeId: selectedProductInformation.successData.originalPartTypeId,
                  productAvailabilityInfo: {
                    aZBadgesFlagVO: selectedProductInformation.aZBadgesFlagVO,
                    dealsInfo: undefined,
                    skuPricingAndAvailability: selectedProductInformation.skuPricingAndAvailability,
                  },
                }}
                miniCartData={headerData?.miniCartMap}
                closeStoreModal={handleCloseMiniCart}
                cartUrl={submitToCart}
                pageName="pdp"
                orderDetailsFromState={orderDetailsFromState}
                parentRef={lazyMiniCartModalRef}
                pageLevelCertonaRecommendations={certonaRecommendations}
              />
            )}
          </div>
        </TrapFocus>
      </Drawer>
    </div>
  ) : null;
}

export const PDPComparisonTable = PDPComparisonTableComp;

function shouldShowValue(value: number | null | undefined) {
  return !!value && value > 0;
}

function getComparisonDisplayPrice({
  skuDeal,
  skuPricingAndAvailability,
  fallbackPrice,
}: ComparisonDisplayPriceOptions): number | undefined {
  const discountedPrice = skuDeal?.skuDealsInfo.dealAvailable
    ? skuDeal.skuDealsInfo.discountedPrice
    : null;

  if (discountedPrice) {
    return Number(discountedPrice);
  }

  if (skuPricingAndAvailability) {
    return skuPricingAndAvailability.retailPrice + skuPricingAndAvailability.corePrice;
  }

  return fallbackPrice;
}

function renderComparisonPrice(displayPrice: number | undefined, isFetchingPrice: boolean) {
  if (shouldShowValue(displayPrice)) {
    return (
      <Price
        priceType="secondary"
        dealPrice={displayPrice?.toFixed(2).split('.') ?? null}
        wrapperClassName={styles.priceWrapper}
        className={styles.price}
      />
    );
  }

  if (isFetchingPrice) {
    return (
      <span className={styles.priceSkeleton} data-testid="comparison-price-skeleton">
        <Skeleton height="26px" width="74px" />
      </span>
    );
  }

  return null;
}
